import {
  Maximize,
  Pause,
  Play,
  RotateCcw,
  Volume2,
  VolumeX,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

export interface OnboardingVideoPlayerCopy {
  playLabel: string;
  pauseLabel: string;
  seekLabel: string;
  muteLabel: string;
  unmuteLabel: string;
  volumeLabel: string;
  fullscreenLabel: string;
  enableAudioLabel: string;
  errorTitle: string;
  retryLabel: string;
}

interface OnboardingVideoPlayerProps extends OnboardingVideoPlayerCopy {
  src: string;
  label: string;
  frameClassName?: string;
}

type WebKitFullscreenVideo = HTMLVideoElement & {
  webkitDisplayingFullscreen?: boolean;
  webkitEnterFullscreen?: () => void | Promise<void>;
  webkitExitFullscreen?: () => void | Promise<void>;
};

const CONTROLS_HIDE_DELAY_MS = 2400;

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

export const OnboardingVideoPlayer = ({
  src,
  label,
  frameClassName,
  playLabel,
  pauseLabel,
  seekLabel,
  muteLabel,
  unmuteLabel,
  volumeLabel,
  fullscreenLabel,
  enableAudioLabel,
  errorTitle,
  retryLabel,
}: OnboardingVideoPlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hideControlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastPointerTypeRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [supportsVolumeControl, setSupportsVolumeControl] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [showAudioPrompt, setShowAudioPrompt] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [supportsFullscreen] = useState(
    () =>
      typeof HTMLElement !== 'undefined' &&
      (typeof HTMLElement.prototype.requestFullscreen === 'function' ||
        (typeof HTMLVideoElement !== 'undefined' &&
          typeof (HTMLVideoElement.prototype as WebKitFullscreenVideo)
            .webkitEnterFullscreen === 'function')),
  );

  const clearHideControlsTimer = useCallback(() => {
    if (hideControlsTimerRef.current !== null) {
      clearTimeout(hideControlsTimerRef.current);
      hideControlsTimerRef.current = null;
    }
  }, []);

  const scheduleControlsHide = useCallback(() => {
    clearHideControlsTimer();
    hideControlsTimerRef.current = setTimeout(() => {
      if (
        mountedRef.current &&
        !containerRef.current?.contains(document.activeElement)
      ) {
        setShowControls(false);
      }
    }, CONTROLS_HIDE_DELAY_MS);
  }, [clearHideControlsTimer]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let active = true;
    mountedRef.current = true;
    video.setAttribute('src', src);

    const start = async () => {
      video.muted = false;
      setIsMuted(false);

      try {
        await video.play();
        if (active) setShowAudioPrompt(false);
        return;
      } catch {
        if (!active) return;
      }

      video.muted = true;
      setIsMuted(true);
      setShowAudioPrompt(true);

      try {
        await video.play();
      } catch {
        if (active) {
          setIsPlaying(false);
          setIsBuffering(false);
          setShowControls(true);
        }
      }
    };

    void start();

    return () => {
      active = false;
      mountedRef.current = false;
      clearHideControlsTimer();
      video.pause();
      video.removeAttribute('src');
      video.load();
    };
  }, [clearHideControlsTimer, src]);

  const playVideo = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      await video.play();
    } catch {
      if (mountedRef.current) setShowControls(true);
    }
  }, []);

  const togglePlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused || video.ended) {
      if (video.ended) {
        video.currentTime = 0;
        setCurrentTime(0);
      }
      void playVideo();
    } else {
      video.pause();
    }
  }, [playVideo]);

  const enableAudio = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.volume === 0) {
      try {
        video.volume = 0.8;
        if (Math.abs(video.volume - 0.8) > 0.01) {
          setSupportsVolumeControl(false);
        }
      } catch {
        setSupportsVolumeControl(false);
      }
    }
    video.muted = false;
    setVolume(video.volume);
    setIsMuted(false);
    setShowAudioPrompt(false);

    if (video.paused) await playVideo();
  }, [playVideo]);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.muted || video.volume === 0) {
      void enableAudio();
    } else {
      video.muted = true;
      setIsMuted(true);
    }
  }, [enableAudio]);

  const toggleFullscreen = useCallback(async () => {
    const container = containerRef.current;
    const video = videoRef.current as WebKitFullscreenVideo | null;
    if (!container || !video || !supportsFullscreen) return;

    setShowControls(true);
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen?.();
      } else if (
        video.webkitDisplayingFullscreen &&
        video.webkitExitFullscreen
      ) {
        await video.webkitExitFullscreen();
      } else if (container.requestFullscreen) {
        await container.requestFullscreen();
      } else {
        await video.webkitEnterFullscreen?.();
      }
    } catch {
      if (mountedRef.current) setShowControls(true);
    }
  }, [supportsFullscreen]);

  const handleKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    const video = videoRef.current;
    if (!video) return;
    const key = event.key.toLowerCase();

    if (event.key === ' ' || key === 'k') {
      event.preventDefault();
      togglePlayback();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      video.currentTime = Math.max(0, video.currentTime - 5);
      setCurrentTime(video.currentTime);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      video.currentTime = Math.min(duration, video.currentTime + 5);
      setCurrentTime(video.currentTime);
    } else if (key === 'm') {
      event.preventDefault();
      toggleMute();
    } else if (key === 'f') {
      event.preventDefault();
      void toggleFullscreen();
    }
  };

  const controlsVisible =
    showControls || !isPlaying || isBuffering || hasError;
  const playbackLabel = isPlaying ? pauseLabel : playLabel;
  const muteControlLabel = isMuted || volume === 0 ? unmuteLabel : muteLabel;

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label={label}
      tabIndex={0}
      onKeyDown={handleKeyboard}
      onFocus={() => {
        clearHideControlsTimer();
        setShowControls(true);
      }}
      onBlur={(event) => {
        if (
          event.relatedTarget instanceof Node &&
          event.currentTarget.contains(event.relatedTarget)
        ) {
          return;
        }
        if (isPlaying && !isBuffering && !hasError) scheduleControlsHide();
      }}
      onPointerMove={() => {
        setShowControls(true);
        if (isPlaying && !isBuffering && !hasError) scheduleControlsHide();
      }}
      onPointerDown={(event) => {
        lastPointerTypeRef.current = event.pointerType || null;
        setShowControls(true);
        if (isPlaying && !isBuffering && !hasError) scheduleControlsHide();
      }}
      onPointerLeave={(event) => {
        const pointerType = event.pointerType || lastPointerTypeRef.current;
        if (pointerType === 'touch') return;
        clearHideControlsTimer();
        if (
          isPlaying &&
          !isBuffering &&
          !hasError &&
          !containerRef.current?.contains(document.activeElement)
        ) {
          setShowControls(false);
        }
      }}
      className={
        frameClassName ??
        'group relative mx-auto aspect-[9/16] w-full max-w-[190px] overflow-hidden rounded-[26px] border border-primary/20 bg-background shadow-[0_24px_80px_-28px_hsl(var(--primary)/0.38)] outline-none ring-offset-background transition-shadow motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:max-w-[250px]'
      }
    >
      <video
        ref={videoRef}
        title={label}
        src={src}
        autoPlay
        playsInline
        preload="metadata"
        className="h-full w-full bg-background object-contain"
        onLoadedMetadata={(event) => {
          setDuration(
            Number.isFinite(event.currentTarget.duration)
              ? event.currentTarget.duration
              : 0,
          );
          setCurrentTime(event.currentTarget.currentTime);
          setHasError(false);
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onVolumeChange={(event) => {
          setVolume(event.currentTarget.volume);
          setIsMuted(event.currentTarget.muted);
        }}
        onPlay={() => {
          setIsPlaying(true);
          setIsBuffering(false);
          scheduleControlsHide();
        }}
        onPause={() => {
          setIsPlaying(false);
          setIsBuffering(false);
          clearHideControlsTimer();
          setShowControls(true);
        }}
        onEnded={() => {
          setIsPlaying(false);
          setIsBuffering(false);
          clearHideControlsTimer();
          setShowControls(true);
        }}
        onWaiting={() => {
          clearHideControlsTimer();
          setIsBuffering(true);
          setShowControls(true);
        }}
        onCanPlay={() => {
          setIsBuffering(false);
          if (isPlaying) scheduleControlsHide();
        }}
        onError={() => {
          clearHideControlsTimer();
          setHasError(true);
          setIsPlaying(false);
          setIsBuffering(false);
          setShowAudioPrompt(false);
          setShowControls(true);
        }}
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_18%,hsl(var(--primary)/0.12),transparent_45%)]"
      />

      {showAudioPrompt && (
        <button
          type="button"
          aria-label={enableAudioLabel}
          onClick={() => void enableAudio()}
          className="absolute left-1/2 top-3 z-30 -translate-x-1/2 whitespace-nowrap rounded-full border border-primary/40 bg-background/85 px-3 py-2 text-xs font-semibold text-foreground shadow-lg backdrop-blur transition-colors motion-reduce:transition-none hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Volume2 className="mr-1.5 inline size-4" />
          {enableAudioLabel}
        </button>
      )}

      {(!isPlaying || isBuffering) && !hasError && (
        <button
          type="button"
          aria-label={playLabel}
          onClick={togglePlayback}
          disabled={isBuffering}
          className="absolute left-1/2 top-1/2 z-20 flex size-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-primary/30 bg-background/80 text-foreground shadow-lg backdrop-blur transition-transform motion-reduce:transition-none hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none"
        >
          {isBuffering ? (
            <RotateCcw className="size-6 animate-spin motion-reduce:animate-none" />
          ) : (
            <Play className="size-6 fill-current" />
          )}
        </button>
      )}

      <div
        data-testid="onboarding-video-controls"
        aria-hidden={!controlsVisible}
        {...(controlsVisible ? {} : { inert: '' })}
        className={`absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-background/95 via-background/75 to-transparent px-1 pb-3 pt-12 transition-opacity motion-reduce:transition-none ${
          controlsVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <input
          type="range"
          aria-label={seekLabel}
          aria-valuetext={`${formatTime(currentTime)} / ${formatTime(duration)}`}
          min={0}
          max={duration}
          step={0.1}
          value={Math.min(currentTime, duration || 0)}
          onChange={(event) => {
            const nextTime = Number(event.currentTarget.value);
            if (!videoRef.current) return;
            videoRef.current.currentTime = nextTime;
            setCurrentTime(nextTime);
          }}
          className="h-1.5 w-full cursor-pointer accent-primary"
        />

        <div
          data-testid="onboarding-video-transport-row"
          className="mt-2 flex w-full flex-nowrap items-center gap-0.5 text-foreground"
        >
          <button
            type="button"
            aria-label={playbackLabel}
            onClick={togglePlayback}
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-full p-0 transition-colors motion-reduce:transition-none hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {isPlaying ? (
              <Pause className="size-4 fill-current" />
            ) : (
              <Play className="size-4 fill-current" />
            )}
          </button>

          <span
            data-testid="onboarding-video-current-time"
            className="shrink-0 text-[11px] tabular-nums text-muted-foreground sm:hidden"
          >
            {formatTime(currentTime)}
          </span>

          <span
            data-testid="onboarding-video-full-time"
            className="hidden shrink-0 text-[11px] tabular-nums text-muted-foreground sm:inline"
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          <span aria-hidden="true" className="min-w-0 flex-1" />

          <button
            type="button"
            aria-label={muteControlLabel}
            onClick={toggleMute}
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-full p-0 transition-colors motion-reduce:transition-none hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {isMuted || volume === 0 ? (
              <VolumeX className="size-4" />
            ) : (
              <Volume2 className="size-4" />
            )}
          </button>

          {supportsVolumeControl && (
            <input
              type="range"
              aria-label={volumeLabel}
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(event) => {
                const video = videoRef.current;
                if (!video) return;
                const nextVolume = Number(event.currentTarget.value);
                let observedVolume = video.volume;
                try {
                  video.volume = nextVolume;
                  observedVolume = video.volume;
                  if (Math.abs(observedVolume - nextVolume) > 0.01) {
                    setSupportsVolumeControl(false);
                  }
                } catch {
                  setSupportsVolumeControl(false);
                }
                setVolume(observedVolume);
                if (nextVolume > 0) {
                  video.muted = false;
                  setIsMuted(false);
                  setShowAudioPrompt(false);
                }
              }}
              className="h-7 w-14 shrink-0 cursor-pointer accent-primary sm:w-20"
            />
          )}

          {supportsFullscreen && (
            <button
              type="button"
              aria-label={fullscreenLabel}
              onClick={() => void toggleFullscreen()}
              className="inline-flex size-7 shrink-0 items-center justify-center rounded-full p-0 transition-colors motion-reduce:transition-none hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Maximize className="size-4" />
            </button>
          )}
        </div>
      </div>

      {hasError && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-background/90 px-5 text-center text-foreground backdrop-blur-sm">
          <p className="text-sm font-semibold">{errorTitle}</p>
          <button
            type="button"
            aria-label={retryLabel}
            onClick={() => {
              const video = videoRef.current;
              if (!video) return;
              setHasError(false);
              setShowControls(true);
              video.load();
              void playVideo();
            }}
            className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-secondary px-4 py-2 text-sm font-semibold text-foreground transition-colors motion-reduce:transition-none hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <RotateCcw className="size-4" />
            {retryLabel}
          </button>
        </div>
      )}
    </div>
  );
};
